import React from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import useSWR from 'swr'

import { getMyZriveReferralRewards } from '../../../api/users'
import BloomLogo from '../../../assets/bloom-logo.svg?react'
import BurgerMenu from '../../../assets/icons/burger-menu.svg?react'
import ExitIcon from '../../../assets/icons/exit.svg?react'
import StarsIcon from '../../../assets/icons/stars.svg?react'
import SearchIcon from '../../../assets/icons/mag-glass.svg?react'
import ArrowTop from '../../../assets/icons/arrow-top.svg?react'
import { CategoryDropdown } from '../../containers/content/categories'
import { useI18N } from '../../../libraries/i18n/hook'
import UserAvatar from '../../containers/users/Avatar'
import './styles.css'
import Dropdown from '../form/Dropdown'
import Button from '../form/Button'
import SearchInput from '../form/search/Input'
import { useSearch } from '../../../libraries/search/hook'
import LinkWithIcon from '../layout/LinkWithIcon'

type Props = {
  onSignOut: () => void
  userFullName?: string
}
const HIDE_SCROLL_THRESHOLD = 40 // Up this point navbar will be visible scrolling down, and hidden scrolling down past that point
const NavBar:React.FC<Props> = ({ onSignOut, userFullName }) => {
  const [showSideMenu, setShowSideMenu] = React.useState<boolean>(false)
  const [visible, setVisible] = React.useState(true)
  const [prevScrollPos, setPrevScrollPos] = React.useState(0)

  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useI18N()
  const { q } = useSearch()
  const { data: rewards = [] } = useSWR('users/zrive-referral-signup/rewards', getMyZriveReferralRewards)
  const hasUnclaimedRewards = rewards.some((r) => !r.claimed && r.readyToClaim)
  const showUnclaimedBanner = hasUnclaimedRewards && location.pathname !== '/zrive-referral-program'

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY
      if (currentScrollPos < HIDE_SCROLL_THRESHOLD) {
        setVisible(true)
        setPrevScrollPos(currentScrollPos)
        return
      }

      const isScrollingUp = prevScrollPos > currentScrollPos
      setVisible(isScrollingUp)
      setPrevScrollPos(currentScrollPos)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [prevScrollPos])

  React.useEffect(() => {
    if (showSideMenu) {
      // close side menu on location
      setShowSideMenu(false)
    }
  }, [location])

  const menuItemClass = 'menu-items text-black hover:text-deep-green'

  const getMenuItems = () => {
    return ([
      <div key='menu-home' className={menuItemClass}><NavItem to='/'>{t('menu.home')}</NavItem></div>,
      <div key='menu-content' className={`leading-none ${menuItemClass}`}><CategoryDropdown label={t('menu.content')} /></div>,
      <div key='menu-courses' className={menuItemClass}><NavItem to='/courses'>{t('courses.title')}</NavItem></div>,
      <div key='menu-jobs' className={menuItemClass}><NavItem to='/jobs'>{t('jobs.title')}</NavItem></div>,
      <div key='menu-companies' className={menuItemClass}><NavItem to='/companies'>{t('companies.title')}</NavItem></div>,
      <div key='menu-events' className={menuItemClass}><NavItem to='/events'>{t('events.title')}</NavItem></div>,
      <div key='menu-favs' className={menuItemClass}><NavItem to='/bookmarks'>{t('menu.bookmarks')}</NavItem></div>,
      // <div key='menu-favorites' className={menuItemClass}><NavItem to='/notifications'>{t('notifications.title')}</NavItem></div>,
    ])
  }

  const getMenuDesktop = () => {
    return (
      <div className='flex items-center gap-x-4'>
        {getMenuItems()}
      </div>
    )
  }

  const toggleMenu = () => {
    setShowSideMenu(!showSideMenu)
  }

  const renderAdvisorAiButton = () => (
    <Button size='md' variant='flat' onClick={() => navigate('/advisor-ai')} icon={<StarsIcon />}>Studioᴬᴵ</Button>
  )

  const renderSearchIcon = () => (
    <Link to='/search-mobile' className='w-7 h-7 mr-5'>
      <SearchIcon className='w-7 h-7' />
    </Link>
  )

  return (
    <>
      <div className={`
        max-w-screen-xl mx-5 sm:mx-auto
        fixed left-0 right-0 z-50 bg-white
        transition-all duration-300
        ${prevScrollPos < 40 ? 'rounded-lg top-[23px]' : 'shadow rounded-b-lg top-0'}
        ${visible ? 'translate-y-0' : '-translate-y-full'}
      `}>
        <nav className='rounded-lg border-0 text-md text-base text-deep-green font-normal max-w-screen-xl mx-auto'>
          <div className='py-2 px-5 w-100 lg:px-8'>
            <div className='border-b-gray-100 border-solid md:border-b justify-between relative w-100 flex flex-row items-center columns-3 md:pb-2'>
              <Link to='/'><BloomLogo className='logo h-8 w-auto' /></Link>
              <div className='hidden md:block w-full max-w-[700px] me-auto ml-14'>
                <SearchInput defaultValue={q} onSubmit={(value) => navigate(`/search?q=${value}`)} placeholder={t('content.searchDesktop')} />
              </div>
              <div className='hidden md:flex justify-end items-center'>
                {renderAdvisorAiButton()}
              </div>
              <div className='hidden md:block text-black'>
                <Dropdown originRight items={[
                  { id: 'profile', item: <Link to='/profile' className='w-full block'>{t('menu.profile')}</Link> },
                  { id: 'logoff', item: <button onClick={onSignOut}>{t('menu.logout')}</button> },
                ]}>
                  <UserAvatar />
                </Dropdown>
              </div>
              <div className='inline-flex items-center md:hidden'>
                {renderSearchIcon()}
                <BurgerMenu onClick={toggleMenu} />
              </div>
            </div>
            <div className='hidden md:inline-block pt-2'>
              {getMenuDesktop()}
            </div>
          </div>
        </nav>
        {showUnclaimedBanner && (
          <div className='border-t border-electric-green/30 bg-electric-green/10 px-5 py-2 lg:px-8 flex items-center justify-center gap-2 flex-wrap text-sm text-deep-green'>
            <span>{t('zriveReferralProgram.bannerUnclaimedMessage')}</span>
            <Link to='/zrive-referral-program#rewards' className='font-semibold underline hover:no-underline'>
              {t('zriveReferralProgram.bannerUnclaimedLink')}
            </Link>
          </div>
        )}
        {showSideMenu && (
          <div className='mobile-menu w-screen flex absolute top-0 right-0 z-50 no-doc-scroll'>
            <div className='w-[20vw] h-screen bg-black opacity-30' onClick={toggleMenu}></div>
            <div className='w-[80vw] h-screen'>
              <div className='w-full h-[15dvh] bg-soft-green flex items-center px-8'>
                <div className='flex items-center'>
                  <UserAvatar size='lg' />
                  <div className='pl-4 text-black max-w-[80%]'>
                    <p className='text-xs'>{t('menu.hello')}</p>
                    <div className='py-1'><strong>{userFullName}</strong></div>
                    <div className='text-xs'><LinkWithIcon linkTo='/profile' label={'Ver cuenta'} icon={<ArrowTop />} /></div>
                  </div>
                </div>
              </div>
              <div className='flex flex-col items-start h-[85dvh] bg-gray-300'>
                <div className='flex flex-col grow items-start gap-y-6 pt-6 px-8'>
                  {getMenuItems()}
                  <div key='studio-ai' className={menuItemClass}><Link to='/advisor-ai'>Studioᴬᴵ</Link></div>
                </div>
                <div className='w-full border-solid border-0 border-t-2 border-soft-green'>
                  <button className='inline-flex gap-x-2 text-black p-8' onClick={onSignOut}>
                      <ExitIcon />{t('menu.logout')}
                    </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={showUnclaimedBanner ? 'h-[118px] md:h-[208px]' : 'h-[70px] md:h-[160px]'} />
    </>
  )
}

type NavItemProps = { to: string, children: React.ReactNode }
const NavItem: React.FC<NavItemProps> = ({ to, children }) => {
  return (
    <NavLink className='md:min-w-16 md:inline-block md:text-center text-black hover:text-deep-green' to={to} aria-current='page'>{children}</NavLink>
  )
}

export default NavBar
